import React, { useEffect, useState } from 'react';
import {
    TableCell,
    TableRow,
    Chip,
    Tooltip,
    IconButton,
    Switch,
    CircularProgress,
    Box,
    Alert,
    Snackbar,
    Typography,
    LinearProgress,
    Popover,
    Divider,
} from '@mui/material';
import { Loader } from '../../../shared/components/Loader';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AppTable } from '../../../shared/components/AppTable';
import { AppTablePagination } from '../../../shared/components/AppTablePagination';
import { AppSearch } from '../../../shared/components/AppSearch';
import { AppButton } from '../../../shared/components/AppButton';
import { getCoupons, deleteCoupon, toggleCouponStatus } from '../../../shared/services/couponService';
import { CouponAttributes } from '../../../shared/types/coupon';
import moment from 'moment';
import { usePermission } from '../../../shared/contexts/PermissionContext';

export const Coupon: React.FC = () => {
    const navigate = useNavigate();
    const { hasPermission, isSuperAdmin } = usePermission();
    const [allCoupons, setAllCoupons] = useState<CouponAttributes[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');
    const [deleteAnchorEl, setDeleteAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [selectedCouponId, setSelectedCouponId] = useState<string | null>(null);

    const fetchCoupons = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await getCoupons({
                page: 1,
                limit: 1000
            });
            if (response.success) {
                setAllCoupons(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching coupons:', error);
            setError('Failed to fetch coupons');
        } finally {
            setLoading(false);
        }
    };

    const filteredCoupons = React.useMemo(() => {
        if (!search.trim()) return allCoupons;
        const q = search.trim().toLowerCase();
        return allCoupons.filter(coupon =>
            (coupon.code?.toLowerCase() || '').includes(q) ||
            (coupon.description?.toLowerCase() || '').includes(q)
        );
    }, [allCoupons, search]);

    const paginatedCoupons = React.useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        return filteredCoupons.slice(start, start + rowsPerPage);
    }, [filteredCoupons, page, rowsPerPage]);

    useEffect(() => {
        fetchCoupons();
    }, []);

    const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
        setDeleteAnchorEl(event.currentTarget);
        setSelectedCouponId(id);
    };

    const handleDeleteClose = () => {
        setDeleteAnchorEl(null);
        setSelectedCouponId(null);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedCouponId) return;
        try {
            const response = await deleteCoupon(selectedCouponId);
            if (response.success) {
                setSuccess('Coupon deleted successfully');
                fetchCoupons();
            }
        } catch (error) {
            console.error('Error deleting coupon:', error);
            setError('Failed to delete coupon');
        } finally {
            handleDeleteClose();
        }
    };

    const handleToggleStatus = async (id: string) => {
        try {
            const response = await toggleCouponStatus(id);
            if (response.success) {
                setSuccess('Coupon status updated');
                fetchCoupons();
            }
        } catch (error) {
            console.error('Error toggling coupon status:', error);
            setError('Failed to update status');
        }
    };

    const handleCloseSnackbar = () => {
        setError('');
        setSuccess('');
    };

    const tableHead = (
        <TableRow className="bg-gray-100">
            <TableCell className="!font-semibold !text-gray-700">Code</TableCell>
            <TableCell className="!font-semibold !text-gray-700">Type</TableCell>
            <TableCell className="!font-semibold !text-gray-700">Value</TableCell>
            <TableCell className="!font-semibold !text-gray-700">Min Order</TableCell>
            <TableCell className="!font-semibold !text-gray-700">Validity</TableCell>
            <TableCell className="!font-semibold !text-gray-700">Usage</TableCell>
            <TableCell className="!font-semibold !text-gray-700">Status</TableCell>
            <TableCell className="!font-semibold !text-gray-700" align="right">Actions</TableCell>
        </TableRow>
    );

    return (
        <div className="flex flex-col flex-auto min-w-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row flex-0 sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b bg-card dark:bg-transparent">
                <div className="flex-1 min-w-0">
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                        Coupons
                    </h2>
                </div>
                <div className="flex items-center gap-4 mt-4 sm:mt-0 flex-wrap">
                    <AppSearch
                        placeholder="Search coupons..."
                        value={search}
                        onChange={(val) => {
                            setSearch(val);
                            setPage(1);
                        }}
                        className="w-full sm:w-64"
                        disabled={loading}
                    />
                    <AppButton
                        variant="secondary"
                        startIcon={<RefreshIcon />}
                        onClick={() => fetchCoupons()}
                        disabled={loading}
                    >
                        Refresh
                    </AppButton>
                    {(isSuperAdmin() || hasPermission('coupons.add')) && (
                        <AppButton
                            variant="primary"
                            startIcon={<AddIcon />}
                            onClick={() => navigate('/coupons/add')}
                        >
                            Add Coupon
                        </AppButton>
                    )}
                </div>
            </div>

            {/* Content Area */}
            <div className="flex flex-auto p-6 sm:p-10">
                <div className="flex flex-col flex-auto min-w-0 w-full">
                    {loading && allCoupons.length > 0 && <LinearProgress className="mb-4" />}
                    {loading && allCoupons.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Loader />
                        </Box>
                    ) : filteredCoupons.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Alert severity="info">
                                {search.trim() ? 'No coupons match your search.' : 'No coupons found.'}
                            </Alert>
                        </Box>
                    ) : (
                        <>
                            <AppTable
                                head={tableHead}
                                isEmpty={filteredCoupons.length === 0}
                                emptyColSpan={8}
                            >
                                {paginatedCoupons.map((coupon, i) => (
                                    <TableRow
                                        key={coupon._id || coupon.id}
                                        hover
                                        className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                                    >
                                        <TableCell>
                                            <Typography variant="body1" fontWeight="medium" className="!text-gray-900">
                                                {coupon.code}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {coupon.description}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={coupon.type.toUpperCase()}
                                                size="small"
                                                color={coupon.type === 'percentage' ? 'secondary' : 'default'}
                                                style={{ borderRadius: '6px' }}
                                            />
                                        </TableCell>
                                        <TableCell className="!text-gray-700">
                                            {coupon.type === 'percentage' ? `${coupon.value}%` : `₹${coupon.value}`}
                                        </TableCell>
                                        <TableCell className="!text-gray-700">₹{coupon.minOrderAmount || 0}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" className="!text-gray-700">
                                                {moment(coupon.startDate).format('DD MMM YYYY')} - {moment(coupon.endDate).format('DD MMM YYYY')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell className="!text-gray-700">
                                            {coupon.usedCount} / {coupon.usageLimit || '∞'}
                                        </TableCell>
                                        <TableCell>
                                            <Switch
                                                checked={coupon.isActive}
                                                onChange={() => handleToggleStatus((coupon._id || coupon.id)!)}
                                                color="primary"
                                                disabled={!isSuperAdmin() && !hasPermission('coupons.update')}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            {(isSuperAdmin() || hasPermission('coupons.update')) && (
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        onClick={() => navigate(`/coupons/edit/${coupon._id || coupon.id}`)}
                                                        size="small"
                                                        className="!text-blue-600 hover:!bg-blue-50"
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {(isSuperAdmin() || hasPermission('coupons.delete')) && (
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        onClick={(e) => handleDeleteClick(e, (coupon._id || coupon.id)!)}
                                                        size="small"
                                                        className="text-red-600 hover:!bg-red-50"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </AppTable>

                            <AppTablePagination
                                totalRows={filteredCoupons.length}
                                page={page - 1}
                                pageSize={rowsPerPage}
                                onPageChange={(_, newPage) => setPage(newPage)}
                                onPageSizeChange={(size) => {
                                    setRowsPerPage(size);
                                    setPage(1);
                                }}
                            />
                        </>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Popover */}
            <Popover
                open={Boolean(deleteAnchorEl)}
                anchorEl={deleteAnchorEl}
                onClose={handleDeleteClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        p: 0,
                        mt: 1.5,
                        width: 250,
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                    },
                }}
            >
                <div className="p-4 bg-white">
                    <Typography className="font-semibold text-gray-900" sx={{ mb: 1 }}>
                        Are you sure?
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        This coupon will be permanently deleted.
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    <div className="flex justify-end gap-2">
                        <AppButton
                            variant="secondary"
                            size="small"
                            onClick={handleDeleteClose}
                        >
                            Cancel
                        </AppButton>
                        <AppButton
                            variant="danger"
                            size="small"
                            onClick={handleDeleteConfirm}
                        >
                            Delete
                        </AppButton>
                    </div>
                </div>
            </Popover>

            <Snackbar
                open={!!error || !!success}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {error ? (
                    <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                        {error}
                    </Alert>
                ) : (
                    <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                        {success}
                    </Alert>
                )}
            </Snackbar>
        </div>
    );
};
